/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Tue Jan 30 2024
 **/

import { useCallback, useEffect } from "react";
import { has, isEmpty, omit } from "lodash-es";
import { atom, useAtom } from "jotai";

import { Properties } from "@/frameworks/libraries/properties";
import { JotaiStore } from "@/frameworks/libraries";
import { useEventHandler, useEventHandlers } from "@rfice/eventhandler";
import { Configure } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import { XMSClientManager } from "@/frameworks/core/unity";
import { useMediaElement, useCommandProducer, usePhoneCall } from "@/frameworks/core/hooks";
import { useNetworkCommand } from "@/frameworks/core/network";
import { useVoiceCall } from "@/frameworks/core/API";
import { useUnityCommand } from "@/frameworks/core/unity";
import type { SubscribeParameter } from "@/frameworks/core/hooks";
import type { VoiceCallRoom, VoiceCallStatusInfo, VoiceCallCommander, VoiceCallResponder } from "./producer.types";
import { EventID } from "@/frameworks/core/commons";
import { useToast } from "@/frameworks/designStuff/components";

const LOGTAG = "#VoiceCallProvider#";

type MessageType = { type: string; payload: VoiceCallStatusInfo };
export const VoiceCallRoomAtom = atom<Record<string, VoiceCallRoom>>({});

export default function VoiceCallProvider() {
	const phoneCall = usePhoneCall();
	const userInfo = {
		get myID() {
			return Configure.userID;
		}
	};
	const [voiceCallRooms, setVoiceCallRooms] = useAtom(VoiceCallRoomAtom, { store: JotaiStore });
	const voiceCallAPI = useVoiceCall();
	const stompCommand = useNetworkCommand();
	const mediaElement = useMediaElement();
	const unityCommand = useUnityCommand();
	const callID = phoneCall.state.active.activeCallID;
	const toast = useToast();

	const onSubscribe = async ({ key }: SubscribeParameter) => {
		if (has(Configure.voiceCallStarter, key)) {
			if (!has(voiceCallRooms, key)) {
				commander.command?.start(key); // 재시작(새로고침)
			} else {
				commander.command?.update_voicecall_data(key, userInfo.myID);
			}
		} else {
			commander.command?.update_voicecall_data(key, userInfo.myID);
		}
	};

	const onUnsubscribe = ({ key }: SubscribeParameter) => {};

	const get_joined = async () => {
		const { data } = await voiceCallAPI.checkJoined.execute();
		return data?.joined || false;
	};

	const startAPI = async (roomID: string) => {
		const joinedAnyVoiceCall = await get_joined();
		console.debug(
			LOGTAG,
			`startAPI(joinedAnyVoiceCall=${joinedAnyVoiceCall})`,
			Configure.voiceCallStarter,
			voiceCallRooms,
			roomID
		);
		if (has(voiceCallRooms, roomID)) {
			try {
				const joined = commander.command?.is_joined(roomID);
				console.debug(LOGTAG, `start(${roomID})`, {
					joined,
					members: commander.command?.get_members(roomID)
				});
				if (joined === false && has(Configure.voiceCallStarter, roomID)) {
					await voiceCallAPI.start.execute({
						roomId: roomID,
						deviceKey: Properties.browserTabID
					});
				} else if (!joinedAnyVoiceCall) {
					await voiceCallAPI.join.execute({ roomId: roomID, deviceKey: Properties.browserTabID });
				} else {
					commander.command?.update_voicecall_data(roomID, userInfo.myID);
				}
			} catch (error: any) {
				if (error.code === "48104") {
					await voiceCallAPI.join.execute({ roomId: roomID, deviceKey: Properties.browserTabID });
				} else {
					console.warn(LOGTAG, `start api 2 failed with`, error);
					commander.command?.update_voicecall_data(roomID, userInfo.myID);
				}
			}
		} else {
			if (joinedAnyVoiceCall) {
				commander.command?.update_voicecall_data(roomID, userInfo.myID);
			} else {
				try {
					if (has(Configure.voiceCallStarter, roomID)) {
						await voiceCallAPI.start.execute({
							roomId: roomID,
							deviceKey: Properties.browserTabID
						});
					} else {
						await voiceCallAPI.join.execute({ roomId: roomID, deviceKey: Properties.browserTabID });
					}
				} catch (error: any) {
					if (error.code === "48104") {
						await voiceCallAPI.join.execute({ roomId: roomID, deviceKey: Properties.browserTabID });
					} else {
						console.warn(LOGTAG, `start api failed with`, error);
					}
				}
			}
		}
	};

	const stopAPI = async (roomID: string) => {
		const joined = commander.command?.is_joined(roomID);
		console.debug(LOGTAG, `stop(${roomID})`, { joined });
		await voiceCallAPI.leave.execute({ roomId: roomID });
	};

	const xmsHandler = useCallback(
		async (type: string, roomID: string, data: any) => {
			switch (type) {
				case "status": {
					console.debug(LOGTAG, `XMSClient Status:`, { type, status: data.status, roomID });
					switch (data.status) {
						case "joined": {
							await startAPI(roomID);
							break;
						}

						case "leave": {
							await stopAPI(roomID);
							break;
						}
					}
					break;
				}

				case "createPeerView": {
					const { userID, stream } = data as { userID: string; stream: MediaStream | null };

					console.debug(
						LOGTAG,
						`stream with peerID(${userID})`,
						stream?.getTracks().map((track) => ({ id: track.id, kind: track.kind, name: track.label }))
					);

					const mediaEl = (() => {
						const result = mediaElement.video.find(`voicecall-${userID}`);
						if (result) {
							return result;
						}
						return mediaElement.create("video", {
							id: `voicecall-${userID}`,
							autoPlay: true,
							muted: true,
							playsInline: true
						});
					})();

					if (mediaEl) {
						mediaEl.muted = false;
						mediaEl.srcObject = stream;
					}
					break;
				}

				case "removePeerView": {
					const { userID } = data as { userID: string };
					mediaElement.video.destroy(`voicecall-${userID}`);
					break;
				}
			}
		},
		[voiceCallRooms]
	);
	const commander = useCommandProducer<VoiceCallCommander, VoiceCallResponder>(
		"VoiceCall",
		{
			async start(roomID: string) {
				console.debug(LOGTAG, `START`, roomID, XMSClientManager.get(roomID));
				if (callID) {
					await phoneCall.call_end(callID ?? "");
				}
				if (!XMSClientManager.get(roomID)) {
					console.debug(LOGTAG, "xms join with", { roomID, myID: userInfo.myID });
					// XMS를 먼저 연결함
					await XMSClientManager.join(roomID, userInfo.myID, xmsHandler, true);
				} else {
					// 이미 연결되어 있을 경우 정보만 업데이트
					await this.update_voicecall_data(roomID, userInfo.myID);
				}
			},
			async stop(roomID: string) {
				if (XMSClientManager.get(roomID)) {
					XMSClientManager.leave(roomID);
				} else {
					console.warn(LOGTAG, `stop called but ${roomID} is not found, so just stop api call`);
					await stopAPI(roomID);
				}
			},

			async start_recording(roomID: string) {
				if (has(voiceCallRooms, roomID)) {
					if (this.is_joined(roomID)) {
						await voiceCallAPI.record.execute({ roomId: roomID, type: "START" });
						XMSClientManager.startRecording(roomID, "voicechat");
					}
				} else {
					console.warn(LOGTAG, `start_recording() failed with room not found`, roomID, voiceCallRooms);
				}
			},
			async stop_recording(roomID: string) {
				try {
					if (has(voiceCallRooms, roomID)) {
						if (this.is_joined(roomID)) {
							await voiceCallAPI.record.execute({ roomId: roomID, type: "END" });
							XMSClientManager.stopRecording(roomID);
						}
					} else {
						console.warn(LOGTAG, `stop_recording() failed with room not found`, roomID, voiceCallRooms);
					}
				} catch (error) {
					console.error(error);
				}
			},

			handleMic(roomID: string, userID: string, micState: MediaState) {
				if (has(voiceCallRooms, roomID)) {
					if (this.is_joined(roomID)) {
						if (micState === "on") {
							XMSClientManager.audioMute(roomID, true);
							stompCommand.deviceMediaState.voiceChat(roomID, {
								id: userID,
								channelID: roomID,
								audio: {
									state: "off"
								}
							});
						} else if (micState === "off") {
							XMSClientManager.audioMute(roomID, false);
							stompCommand.deviceMediaState.voiceChat(roomID, {
								id: userID,
								channelID: roomID,
								audio: {
									state: "on"
								}
							});
						} else {
							//micState none
						}
					}
				} else {
					console.warn(LOGTAG, `stop_recording() failed with room not found`, roomID, voiceCallRooms);
				}
			},
			update_mic_status(roomID: string, userID: string) {},

			async update_voicecall_data(roomID: string, userID: string) {
				const oldData = { ...(has(voiceCallRooms, roomID) ? voiceCallRooms[roomID] : {}) };
				const { data } = await voiceCallAPI.inquiry.execute({ roomId: roomID });
				const updateData = {
					...oldData,
					userID,
					recording: data?.recording || false,
					recordStartTime: data?.recordStartTime || 0,
					members:
						data?.joinedMembers.map((member) => ({
							id: member.id,
							name: member.name,
							micState: oldData.members?.find((user) => user.id === member?.id)?.micState ?? "on"
						})) || []
				} as VoiceCallRoom;
				if (
					data?.joinedMembers &&
					data.joinedMembers.map((member) => member?.id).includes(Properties.property?.user?.userId || "")
				) {
					unityCommand.voiceCallState({ state: "started" });
					unityCommand.backgroundSoundState({ action: "mute", reason: "voicechat" });
					Properties.property = {
						currentMediaConnectionState: "voicechat"
					};
				} else {
					unityCommand.backgroundSoundState({ action: "unmute", reason: "voicechat" });
					unityCommand.voiceCallState({ state: "ended" });
					Properties.property = {
						currentMediaConnectionState: null
					};
				}
				setVoiceCallRooms((prev) => ({ ...prev, [roomID]: { ...prev[roomID], ...updateData } }));
				return updateData;
			},

			get_members(roomID: string) {
				if (has(voiceCallRooms, roomID)) {
					return voiceCallRooms[roomID].members;
				}
				return [];
			},

			is_joined(roomID: string) {
				return (
					(roomID?.length > 0 &&
						this.get_members(roomID)?.findIndex((member) => member.id === userInfo.myID) >= 0) ||
					false
				);
			},
			is_recording(roomID: string) {
				if (has(voiceCallRooms, roomID)) {
					return (roomID?.length > 0 && voiceCallRooms[roomID].recording) || false;
				}
				return false;
			},
			is_mic_on(roomID: string, userID: string) {
				if (has(voiceCallRooms, roomID)) {
					return voiceCallRooms[roomID].members.filter((member) => member.id === userID)[0]?.micState;
				}
			}
		},
		{ onSubscribe, onUnsubscribe },
		[voiceCallRooms, callID]
	);

	useEffect(() => {
		Object.entries(voiceCallRooms).forEach(async ([roomID, room]) => {
			const consumer = commander.get(roomID);
			consumer?.onVoiceCallRoomData?.(room);

			if (room.userID === userInfo.myID) {
				consumer?.onVoiceCallJoined?.(commander.command?.is_joined(roomID));
				room.recording && consumer?.onVoiceRecordStarted?.(room.recordStartTime);
			}

			room.members?.length > 1 &&
				XMSClientManager.changeTalkingGroup(
					roomID,
					room.members.map((member) => member.id)
				);
		});
	}, [voiceCallRooms]);

	useEventHandler(
		EventID.STOP_VOICECHAT_BY_PRIORITY,
		() => {
			if (voiceCallRooms) {
				const roomID = Object.keys(voiceCallRooms)[0];
				const members = Object.values(voiceCallRooms);
				if (roomID && !isEmpty(members?.[0]?.members)) {
					commander.command.stop(roomID);
				}
			}
		},
		[voiceCallRooms]
	);

	useEventHandlers(
		{
			async onVoiceCallStarting(roomID: string) {
				const consumer = commander.get(roomID);
				consumer?.onVoiceCallLoading?.(true);

				console.debug(LOGTAG, `voicecall starting with`, roomID, userInfo.myID);
				Configure.voiceCallStarter = { [roomID]: userInfo.myID };
				await commander.command?.start(roomID);
			},
			async onVoiceCallMessage(messageType: ChatServices.MessageIDs, { payload }: MessageType, topic: string) {
				const { type, roomId, member, channelID, ...rest } = payload;
				console.debug(LOGTAG, `${messageType}`, payload);

				switch (messageType) {
					case "voiceChatStatusUpdated": {
						if (type === "START") {
							await commander.command?.update_voicecall_data(roomId, member.id);
						} else if (type === "END") {
							const consumer = commander.get(roomId);
							if (consumer) {
								consumer?.onVoiceCallJoined?.(false);
								setVoiceCallRooms(omit(voiceCallRooms, roomId));
								Configure.voiceCallStarter = omit(Configure.voiceCallStarter, roomId);
								if (XMSClientManager.get(roomId)) {
									// 1분 경과로 자동 종료 시 leave 처리
									toast.show({
										type: "system",
										message: _T("보이스챗 참여자가 없어 자동으로 종료됩니다.")
									});
									XMSClientManager.leave(roomId);
								}
							}
							if (member.id === Configure.userID) {
								unityCommand.voiceCallState({ state: "ended" });
								unityCommand.backgroundSoundState({ action: "unmute", reason: "voicechat" });
								Properties.property = {
									currentMediaConnectionState: null
								};
							}
						}
						break;
					}

					case "voiceChatJoined": {
						console.debug(LOGTAG, `user joined`, roomId, member);

						await commander.command?.update_voicecall_data(roomId, member.id);
						break;
					}

					case "voiceChatLeft": {
						console.debug(LOGTAG, `user leaved`, roomId, rest.memberId || "");
						// 현재 인원을 보고 한명이 남아있을 때 voiceChatLeft를 받은 인원이 나가면 모두 나가는 것으로 판단하고 update 시키지않고,
						// voiceChatUpdate status:END로 상태가 온다는 판단하에 작업하였습니다.
						if (commander.command?.get_members(roomId)?.length === 1) {
							return;
						}
						await commander.command?.update_voicecall_data(roomId, rest.memberId || "");
						break;
					}

					case "voiceChatRecordStatusUpdated": {
						console.debug(LOGTAG, `user voiceCallRecordStatusUpdated`, payload);
						const consumer = commander.get(roomId);
						const updatedData = await commander.command?.update_voicecall_data(roomId, rest.memberId || "");
						if (type === "START") {
							consumer?.onVoiceRecordStarted?.(updatedData.recordStartTime || new Date().getTime());
						} else {
							consumer?.onVoiceRecordEnded?.(updatedData.recordStartTime || new Date().getTime());
						}
						break;
					}

					case "deviceMediaState": {
						// 상대방의 마이크 상태를 업데이트 시켜야함.
						setVoiceCallRooms((prev) => ({
							...prev,
							[channelID]: {
								...prev[channelID],
								members: prev[channelID]?.members?.map((member) =>
									member.id === payload.id ? { ...member, micState: payload.audio?.state } : member
								)
							}
						}));
						break;
					}
				}
			}
		},
		[voiceCallRooms, commander]
	);

	return null; // <RemoteVideo ref={mediaRef} auÏtoPlay muted playsInline />;
}
